// src/pages/Users.jsx
import React, { useEffect, useState } from 'react';
import { Users as UsersIcon, Search, UserPlus, XCircle, Pencil, Trash2, Loader2 } from 'lucide-react';
import { adminUsersAPI } from '../api';

const emptyForm = {
  name: '',
  email: '',
  password: '',
  type: 'advertiser',
  company: ''
};

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [signupType, setSignupType] = useState('all');
  const [actionError, setActionError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [activeUser, setActiveUser] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [bulkProcessing, setBulkProcessing] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 350);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    fetchUsers();
  }, [debouncedSearchTerm, signupType]);

  useEffect(() => {
    setSelectedUserIds((prev) => prev.filter((id) => users.some((user) => user.id === id)));
  }, [users]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        limit: 100
      };

      if (debouncedSearchTerm.trim()) {
        params.search = debouncedSearchTerm.trim();
      }

      if (signupType !== 'all') {
        params.type = signupType;
      }

      const response = await adminUsersAPI.list(params);
      const userRows = response?.data?.users || response?.users || [];
      setUsers(userRows);
    } catch (fetchError) {
      console.error('Failed to fetch users:', fetchError);
      setError(fetchError.message);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setModalMode('create');
    setActiveUser(null);
    setFormData(emptyForm);
    setActionError('');
    setModalOpen(true);
  };

  const openEditModal = (user) => {
    setModalMode('edit');
    setActiveUser(user);
    setFormData({
      name: user.name || '',
      email: user.email || '',
      password: '',
      type: user.type || 'advertiser',
      company: user.company || ''
    });
    setActionError('');
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setActionError('');
    setFormData(emptyForm);
    setActiveUser(null);
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveUser = async (event) => {
    event.preventDefault();
    setActionError('');
    setSuccessMessage('');

    if (!formData.name.trim() || !formData.email.trim() || (modalMode === 'create' && !formData.password.trim())) {
      setActionError('Name, email, and password are required for new users.');
      return;
    }

    try {
      setSubmitting(true);

      if (modalMode === 'create') {
        await adminUsersAPI.create({
          name: formData.name.trim(),
          email: formData.email.trim(),
          password: formData.password,
          type: formData.type,
          company: formData.company.trim() || null
        });
        setSuccessMessage('User created successfully.');
      } else if (activeUser) {
        const updatePayload = {
          name: formData.name.trim(),
          type: formData.type,
          company: formData.company.trim() || null
        };

        if (formData.password.trim()) {
          updatePayload.password = formData.password;
        }

        await adminUsersAPI.update(activeUser.id, {
          ...updatePayload
        });
        setSuccessMessage('User updated successfully.');
      }

      closeModal();
      await fetchUsers();
    } catch (requestError) {
      setActionError(requestError.message || 'Unable to save user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteUser = async (user) => {
    setActionError('');
    setSuccessMessage('');

    const confirmed = window.confirm(`Delete ${user.name || user.email}? This cannot be undone.`);
    if (!confirmed) {
      return;
    }

    try {
      await adminUsersAPI.delete(user.id);
      setSuccessMessage('User deleted successfully.');
      await fetchUsers();
    } catch (requestError) {
      setActionError(requestError.message || 'Unable to delete user');
    }
  };

  const isAllSelected = users.length > 0 && selectedUserIds.length === users.length;
  const isSomeSelected = selectedUserIds.length > 0 && !isAllSelected;

  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedUserIds([]);
      return;
    }

    setSelectedUserIds(users.map((user) => user.id));
  };

  const toggleSelectUser = (userId) => {
    setSelectedUserIds((prev) => {
      if (prev.includes(userId)) {
        return prev.filter((id) => id !== userId);
      }

      return [...prev, userId];
    });
  };

  const handleBulkApprovalStatus = async (status) => {
    if (selectedUserIds.length === 0) {
      setActionError('Select at least one user first.');
      return;
    }

    const confirmed = window.confirm(`Set ${selectedUserIds.length} selected user(s) to ${status}?`);
    if (!confirmed) {
      return;
    }

    setActionError('');
    setSuccessMessage('');

    try {
      setBulkProcessing(true);
      const updates = await Promise.allSettled(
        selectedUserIds.map((id) => adminUsersAPI.update(id, { approval_status: status }))
      );

      const successCount = updates.filter((result) => result.status === 'fulfilled').length;
      const failedCount = updates.length - successCount;

      if (successCount > 0) {
        await fetchUsers();
      }

      setSelectedUserIds([]);

      if (failedCount === 0) {
        setSuccessMessage(`Updated ${successCount} user(s) to ${status}.`);
      } else {
        setActionError(`Updated ${successCount} user(s), but ${failedCount} update(s) failed.`);
      }
    } catch (requestError) {
      setActionError(requestError.message || 'Unable to run bulk approval update');
    } finally {
      setBulkProcessing(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedUserIds.length === 0) {
      setActionError('Select at least one user first.');
      return;
    }

    const confirmed = window.confirm(`Delete ${selectedUserIds.length} selected user(s)? This cannot be undone.`);
    if (!confirmed) {
      return;
    }

    setActionError('');
    setSuccessMessage('');

    try {
      setBulkProcessing(true);
      const deletions = await Promise.allSettled(
        selectedUserIds.map((id) => adminUsersAPI.delete(id))
      );

      const successCount = deletions.filter((result) => result.status === 'fulfilled').length;
      const failedCount = deletions.length - successCount;

      if (successCount > 0) {
        await fetchUsers();
      }

      setSelectedUserIds([]);

      if (failedCount === 0) {
        setSuccessMessage(`Deleted ${successCount} user(s).`);
      } else {
        setActionError(`Deleted ${successCount} user(s), but ${failedCount} deletion(s) failed.`);
      }
    } catch (requestError) {
      setActionError(requestError.message || 'Unable to run bulk delete');
    } finally {
      setBulkProcessing(false);
    }
  };

  const handleApprovalStatusChange = async (user, status) => {
    setActionError('');
    setSuccessMessage('');

    try {
      setStatusUpdatingId(user.id);
      await adminUsersAPI.update(user.id, { approval_status: status });
      setUsers((prev) => prev.map((item) => (item.id === user.id ? { ...item, approval_status: status } : item)));
      setSuccessMessage(`Updated ${user.name || user.email} to ${status}.`);
    } catch (requestError) {
      setActionError(requestError.message || 'Unable to update approval status');
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const getTypeBadgeClass = (type) => {
    if (type === 'affiliate') {
      return 'text-emerald-300 bg-emerald-900/30 border border-emerald-700/40';
    }

    return 'text-blue-300 bg-blue-900/30 border border-blue-700/40';
  };

  const getApprovalBadgeClass = (status) => {
    if (status === 'approved') {
      return 'text-emerald-300 bg-emerald-900/30 border border-emerald-700/40';
    }

    if (status === 'rejected') {
      return 'text-red-300 bg-red-900/30 border border-red-700/40';
    }

    return 'text-amber-300 bg-amber-900/30 border border-amber-700/40';
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-slate-100">User Management</h2>
          <p className="text-slate-400 mt-2">Manage sign-ups and filter by advertiser or affiliate</p>
        </div>
        <button
          onClick={openCreateModal}
          className="px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white font-medium rounded-lg transition-all duration-200 flex items-center"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Add User
        </button>
      </div>

      {successMessage && (
        <div className="rounded-lg border border-emerald-700/50 bg-emerald-900/20 px-4 py-3 text-emerald-300 text-sm">
          {successMessage}
        </div>
      )}

      {actionError && (
        <div className="rounded-lg border border-red-700/50 bg-red-900/20 px-4 py-3 text-red-300 text-sm">
          {actionError}
        </div>
      )}

      <div className="admin-glass rounded-xl p-4 border border-slate-700/50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-sm text-slate-300">
          {selectedUserIds.length > 0
            ? `${selectedUserIds.length} selected`
            : 'Select users to run bulk actions'}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => handleBulkApprovalStatus('approved')}
            disabled={selectedUserIds.length === 0 || bulkProcessing}
            className="px-3 py-1.5 rounded-md bg-emerald-700/30 border border-emerald-600/50 text-emerald-200 text-xs hover:bg-emerald-700/40 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Bulk Approve
          </button>
          <button
            onClick={() => handleBulkApprovalStatus('rejected')}
            disabled={selectedUserIds.length === 0 || bulkProcessing}
            className="px-3 py-1.5 rounded-md bg-amber-700/30 border border-amber-600/50 text-amber-200 text-xs hover:bg-amber-700/40 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Bulk Reject
          </button>
          <button
            onClick={handleBulkDelete}
            disabled={selectedUserIds.length === 0 || bulkProcessing}
            className="px-3 py-1.5 rounded-md bg-red-700/30 border border-red-600/50 text-red-200 text-xs hover:bg-red-700/40 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Bulk Delete
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="admin-glass rounded-xl p-6 border border-slate-700/50">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>
          <select
            value={signupType}
            onChange={(e) => setSignupType(e.target.value)}
            className="px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            <option value="all">All Sign-ups</option>
            <option value="advertiser">Advertisers</option>
            <option value="affiliate">Affiliates</option>
          </select>
        </div>
      </div>

      {/* Users Table */}
      <div className="admin-glass rounded-xl border border-slate-700/50 overflow-hidden">
        <div className="p-6">
          <h3 className="text-xl font-bold text-slate-100 mb-4">Sign-ups ({users.length})</h3>

          {loading ? (
            <div className="h-96 flex items-center justify-center">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500 mx-auto mb-4"></div>
                <p className="text-slate-400">Loading users...</p>
              </div>
            </div>
          ) : error ? (
            <div className="h-96 flex items-center justify-center">
              <div className="text-center">
                <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                <p className="text-red-400 text-lg">Error loading users</p>
                <p className="text-slate-500">{error}</p>
                <button
                  onClick={fetchUsers}
                  className="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  Retry
                </button>
              </div>
            </div>
          ) : users.length === 0 ? (
            <div className="h-96 flex items-center justify-center border-2 border-dashed border-slate-700 rounded-lg">
              <div className="text-center">
                <UsersIcon className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400 text-lg">No sign-ups found</p>
                <p className="text-slate-500">New advertiser and affiliate sign-ups will appear here</p>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 px-4 text-slate-300 font-medium w-12">
                      <input
                        type="checkbox"
                        checked={isAllSelected}
                        ref={(element) => {
                          if (element) {
                            element.indeterminate = isSomeSelected;
                          }
                        }}
                        onChange={toggleSelectAll}
                        className="h-4 w-4 rounded border-slate-500 bg-slate-800 text-red-500 focus:ring-red-500"
                        disabled={bulkProcessing}
                      />
                    </th>
                    <th className="text-left py-3 px-4 text-slate-300 font-medium">Name</th>
                    <th className="text-left py-3 px-4 text-slate-300 font-medium">Email</th>
                    <th className="text-left py-3 px-4 text-slate-300 font-medium">Sign-up Type</th>
                    <th className="text-left py-3 px-4 text-slate-300 font-medium">Company</th>
                    <th className="text-left py-3 px-4 text-slate-300 font-medium">Approval</th>
                    <th className="text-left py-3 px-4 text-slate-300 font-medium">Date</th>
                    <th className="text-right py-3 px-4 text-slate-300 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id} className="border-b border-slate-800 hover:bg-slate-800/30">
                      <td className="py-4 px-4">
                        <input
                          type="checkbox"
                          checked={selectedUserIds.includes(user.id)}
                          onChange={() => toggleSelectUser(user.id)}
                          className="h-4 w-4 rounded border-slate-500 bg-slate-800 text-red-500 focus:ring-red-500"
                          disabled={bulkProcessing}
                        />
                      </td>
                      <td className="py-4 px-4 text-slate-100 font-medium">{user.name}</td>
                      <td className="py-4 px-4 text-slate-300">{user.email}</td>
                      <td className="py-4 px-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getTypeBadgeClass(user.type)}`}>
                          {user.type}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-slate-300">{user.company || '-'}</td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getApprovalBadgeClass(user.approval_status || 'approved')}`}>
                            {user.approval_status || 'approved'}
                          </span>
                          <select
                            value={user.approval_status || 'approved'}
                            onChange={(e) => handleApprovalStatusChange(user, e.target.value)}
                            className="px-2 py-1 bg-slate-800 border border-slate-600 rounded-md text-xs text-slate-200 focus:outline-none focus:ring-2 focus:ring-red-500"
                            disabled={statusUpdatingId === user.id}
                          >
                            <option value="pending">Pending</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                          </select>
                        </div>
                      </td>
                      <td className="py-4 px-4 text-slate-400 text-sm">
                        {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openEditModal(user)}
                            className="inline-flex items-center px-2.5 py-1.5 rounded-md bg-slate-700 hover:bg-slate-600 text-slate-100 text-xs"
                          >
                            <Pencil className="w-3.5 h-3.5 mr-1" />
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteUser(user)}
                            className="inline-flex items-center px-2.5 py-1.5 rounded-md bg-red-900/40 hover:bg-red-800/50 border border-red-700/60 text-red-200 text-xs"
                          >
                            <Trash2 className="w-3.5 h-3.5 mr-1" />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-xl rounded-xl border border-slate-700 bg-slate-900 shadow-2xl">
            <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-100">
                {modalMode === 'create' ? 'Add User' : 'Edit User'}
              </h3>
              <button
                onClick={closeModal}
                className="text-slate-400 hover:text-slate-200"
                disabled={submitting}
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveUser} className="p-6 space-y-4">
              {actionError && (
                <div className="rounded-lg border border-red-700/50 bg-red-900/20 px-4 py-3 text-red-300 text-sm">
                  {actionError}
                </div>
              )}

              <div>
                <label className="block text-sm text-slate-300 mb-1">Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-red-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm text-slate-300 mb-1">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleFormChange('email', e.target.value)}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-red-500"
                  required
                  disabled={modalMode === 'edit'}
                />
              </div>

              {modalMode === 'create' && (
                <div>
                  <label className="block text-sm text-slate-300 mb-1">Password</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => handleFormChange('password', e.target.value)}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-red-500"
                    required
                  />
                </div>
              )}

              {modalMode === 'edit' && (
                <div>
                  <label className="block text-sm text-slate-300 mb-1">Reset Password (optional)</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => handleFormChange('password', e.target.value)}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-red-500"
                    placeholder="Leave blank to keep current password"
                  />
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-slate-300 mb-1">User Type</label>
                  <select
                    value={formData.type}
                    onChange={(e) => handleFormChange('type', e.target.value)}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    <option value="advertiser">Advertiser</option>
                    <option value="affiliate">Affiliate</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-slate-300 mb-1">Company</label>
                  <input
                    type="text"
                    value={formData.company}
                    onChange={(e) => handleFormChange('company', e.target.value)}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                </div>
              </div>

              <div className="pt-2 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-100"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-500 text-white font-medium inline-flex items-center"
                  disabled={submitting}
                >
                  {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {modalMode === 'create' ? 'Create User' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
